import { TFunction } from "i18next"

import {
  buildCustomTableBody,
  buildCustomTableDisplayColumns,
  buildCustomTableHeader,
  buildCustomTableRowSources,
  buildCustomTableSummaryRows,
  createCompareByGroupKey,
  mapTableBodyToRows,
  resolveSummaryRowKinds,
  sortCustomTableRows,
} from "@/app/report/hooks/useCustomTableData"
import { MetricMapType, StatsSettings } from "@/app/report/common/type"
import { REPORT_TYPE } from "@/app/report/common/constants"
import { buildCSVString } from "@/app/report/common/export/exportTable"
import { TrendGroupSummaryRow } from "@/types/report"
import { CalculatedMetric, Unit } from "@/types/setting"

/**
 * Custom Table 을 렌더 없이 CSV 로 만든다.
 * 데이터 구성은 화면/PDF(useCustomTableData)와 공유하는 순수 빌더를 그대로 사용하고,
 * 정렬은 템플릿 sortSetting, 없으면 groupKey 기본 정렬(화면 초기 순서와 동일).
 * 헤더 제목·빈 셀 규칙은 화면 CSV(CustomTableSection.exportTable → exportTableFromView)와 동일.
 * 상단 Total/Average 요약 2행(#98)도 화면과 같은 빌더로 만든다 — 일괄 내보내기는 컬럼 필터가 없으므로 전체 행 기준.
 */
export function buildCustomTableCSV(params: {
  statsSettings: StatsSettings
  // 선수 그룹의 표시 컬럼이 리포트 종류에 따라 다르다(Trend 는 참여 활동 수 포함) — 화면과 같은 컬럼을 내려면 필요 (#99)
  reportType?: REPORT_TYPE
  groupSummaryData: TrendGroupSummaryRow[]
  metricMap: MetricMapType
  selectedUnitList?: Unit[]
  calculatedMetrics?: CalculatedMetric[]
  positionRank: (code?: string | number) => number
  positionGroupRank: (group?: string) => number
  t: TFunction
  translateKey: (key: string) => string | undefined
}): string {
  const {
    statsSettings,
    reportType,
    groupSummaryData,
    metricMap,
    selectedUnitList,
    calculatedMetrics,
    positionRank,
    positionGroupRank,
    t,
    translateKey,
  } = params

  const displayColumns = buildCustomTableDisplayColumns(statsSettings, reportType)
  const tableHeader = buildCustomTableHeader(
    statsSettings,
    displayColumns,
    metricMap,
    selectedUnitList,
  )
  const tableBody = buildCustomTableBody(
    statsSettings,
    groupSummaryData ?? [],
    tableHeader,
    displayColumns,
    metricMap,
  )

  const rows = sortCustomTableRows(
    mapTableBodyToRows(tableBody, tableHeader),
    statsSettings.sortSetting,
    statsSettings.groupKey1,
    statsSettings.groupKey2,
    createCompareByGroupKey(positionRank, positionGroupRank),
  )

  const summaryRows = buildCustomTableSummaryRows({
    tableHeader,
    displayColumnCount: displayColumns.length,
    rowSources: buildCustomTableRowSources(statsSettings, groupSummaryData ?? []),
    metricMap,
    selectedUnitList,
    calculatedMetrics,
    t,
    kinds: resolveSummaryRowKinds(statsSettings.summaryRows),
  })

  return buildCSVString(
    // 헤더 제목은 title 키 기준 — 선수 이름 컬럼이 화면·PDF 와 같은 "Player Name" 으로 나온다 (#99-4)
    tableHeader.map((header) => ({
      title: translateKey(header.title) || t(header.title),
      unit: header.unit,
    })),
    [...summaryRows, ...rows.map((row) => tableHeader.map((header) => row[header.key] ?? ""))],
  )
}
